/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { useEffect, useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { CARD_ACTIONS } from 'carbon-addons-iot-react';
import classnames from 'classnames';
import { defineMessages, useIntl } from 'react-intl';

import { DIALOG_IDS, ROUTES } from '../../constants/constants';
import CreateServiceRequestModal from '../../components/CreateServiceRequestModal/CreateServiceRequestModal';
import ChangeAlertStatusModal from '../../components/ChangeAlertStatusModal/ChangeAlertStatusModal';
import ChangeAlertOwnerModal from '../../components/ChangeAlertOwnerModal/ChangeAlertOwnerModal';
import { PickerKinds } from '../../types/DateTypes';
import { useEntityTypeName } from '../../hooks/entityTypeHooks';
import {
  updateAlertsAPI,
  fetchHierarchyAlerts,
  getParentAssetsForDeviceAPI,
  getParentLocationsForDeviceAPI,
  getDimensionsForDeviceAPI,
  getAssetByIdForSiteAPI,
  getLocationByIdForSiteAPI,
  getAssetsForLocationAPI,
  getSiteByIdAPI,
} from '../../../Setup/services/HierarchiesAPI';
import { formatAlertsForHierarchyAPI } from '../../utils/alertUtils';
import { RESOURCE_TYPES } from '../../../Setup/constants/constants';
import AlertsTable from '../../components/AlertsTable/AlertsTable';
import {
  validateAssetRelationsAPI,
  createServiceRequestAPI,
} from '../../services/ServiceRequestActionAPI';
import { AlertsType } from '../../types/AlertsTypes';

import { getEntityTypes, getEntityTypesFetchingData } from 'entityTypesReducer';
import { upsert } from 'Utils';
import * as userActionCreators from 'userDashboardActionCreators';
import * as entityTypeActions from 'entityTypeActionCreators';
import * as domainActionCreators from 'domainActionCreators';
import * as serviceActionCreators from 'dashboardActionCreators';
import * as commonActions from 'actionCreators';
import { getDomains, isDomainsFetching } from 'domainReducer';
import { getTenantMembers, getTenantMembersIsLoading } from 'usersReducer';
import { getCurrentUser, getCurrentUserAccessControls, getCurrentUserResources } from 'userReducer';
import { getMaximoServices, isOtherServicesFetchingData } from 'otherServicesReducer';
import { getDeviceTypesData } from 'hierarchiesReducer';
import {
  getDeviceTypeFromHierarchyPath,
  getLeafIdFromHierarchyPath,
  getHierarchyItemTypeFromHierarchyPath,
  getSiteFromHierarchyPath,
} from 'hierarchyUtils';
import { hasBetaPreviewEnabled } from 'localStorageUtils';
import { AppDispatch, RootState } from 'store';
import { ViewColumnType } from 'TableTypes';
import { DomainsType } from 'KPITypes';
import { DeviceTypeResourceType } from 'HierarchyActionTypes';
import { CommonActionsType, UserResourcesType } from 'CommonTypes';
import { EntityTypesType } from 'EntitiesTypes';

const messages = defineMessages({
  last30Minutes: 'Last 30 minutes',
  lastHour: 'Last hour',
  last6Hours: 'Last 6 hours',
  last12Hours: 'Last 12 hours',
  last24Hours: 'Last 24 hours',
});

interface AlertsTableContainerType {
  /** user information so we can track who is updating the record */
  currentUser: any;

  currentUserResources?: UserResourcesType;
  /** should the alerts be filtered by entity type id */
  entityTypeId?: string;
  /** should the alerts be filtered by entity id */
  entityId?: string;
  /** should the alerts be filtered by dataItemName */
  dataItemName?: string;
  // Actions to fetch the necessary data
  entityTypeActions: { fetchEntityTypesData: any };
  domainActions: { fetchDomains: any };
  userActions: { loadTenantMembers: any };
  serviceActions: { getOtherServices: any };
  actions: CommonActionsType;
  /** set of access controls */
  accessControls?: Array<string>;
  onCardAction?: (cardId: string, actionType: string, payload: any) => void;
  id?: string;
  className?: string;
  isExpanded?: boolean;
  domains: {
    Status: DomainsType;
    Severity: DomainsType;
  };
  users?: Array<{ email: string }>;
  isPreview?: boolean;
  dashboardData?: AlertsType;
  services: any;
  /** needed while we're fetching the additional data */
  isFetchingAdditionalData?: boolean;
  isFetchingDomainData?: boolean;
  /** Id that can be used for testing */
  testId?: string;
  columnsState?: ViewColumnType[];
  assetTypes?: EntityTypesType;
  hasLargeNoData?: boolean;
  title?: React.ReactNode;
  tooltip?: JSX.Element;
  customToolbarContent?: React.ReactNode;
  size?: { height: number; width: number };
  hasShortRows?: boolean;
  numberOfRowsPerPage?: number;
  /** uses CommonTableWithViews vs CommonTable */
  hasTableViewManagement?: boolean;
  isHierarchyDashboard?: boolean;
  hierarchyPath?: string;
  deviceTypes?: Array<DeviceTypeResourceType>;
}

const defaultProps = {
  id: null,
  entityTypeId: null,
  entityId: null,
  dataItemName: null,
  accessControls: [],
  users: [],
  isPreview: false,
  isFetchingAdditionalData: false,
  isFetchingDomainData: false,
  className: null,
  isExpanded: false,
  onCardAction: null,
  dashboardData: null,
  testId: 'alert-table-containter-content',
  columnsState: null,
  assetTypes: [],
  hasLargeNoData: false,
  title: null,
  tooltip: null,
  customToolbarContent: null,
  size: {},
  hasShortRows: false,
  numberOfRowsPerPage: 10,
  hasTableViewManagement: false,
  isHierarchyDashboard: false,
  hierarchyPath: '',
  deviceTypes: [],
  currentUserResources: undefined,
};

/**
 * Sets up environment for the alert table
 * Renders and supplies handlers for alert table modals
 * Fetches alerts
 */
const AlertsTableContainer = ({
  domainActions: { fetchDomains },
  userActions: { loadTenantMembers },
  serviceActions: { getOtherServices },
  actions,
  domains,
  users,
  // only used if the alert table has view enabled
  entityTypeActions: { fetchEntityTypesData },
  assetTypes,
  services,
  currentUser: { id },
  currentUserResources,
  isHierarchyDashboard,
  hierarchyPath,
  accessControls,
  dataItemName,
  entityId,
  dashboardData,
  hasLargeNoData,
  entityTypeId,
  onCardAction,
  id: cardId,
  className,
  isPreview,
  isExpanded,
  testId,
  isFetchingAdditionalData,
  isFetchingDomainData,
  title,
  tooltip,
  customToolbarContent,
  size,
  columnsState, // coming from dashboard or dashboard editor
  hasShortRows,
  numberOfRowsPerPage,
  hasTableViewManagement,
  deviceTypes,
}: AlertsTableContainerType): JSX.Element => {
  const { formatMessage } = useIntl();

  const [isFetchingData, setIsFetchingData] = useState(false);
  const [entityAlerts, setEntityAlerts] = useState(null);
  const [hierarchyLevelAlerts, setHierarchyLevelAlerts] = useState(null);

  // the view being displayed
  const [selectedView, setSelectedView] = useState(null);

  // Need to get the device type UUID needed for some v2 API calls
  // because hierarchyPath is not always available
  // entityTypeId is actually entityTypeName
  const [entityTypeUUId, entityTypes] = useEntityTypeName(entityTypeId, id, deviceTypes);

  // available relative options for the TableDateSelector
  // need to do this here so that the default can be set correctly
  const timeRangeOptions = useMemo(
    () => [
      {
        id: 'last30Minutes',
        label: formatMessage(messages.last30Minutes),
        offset: 30,
      },
      {
        id: 'lastHour',
        label: formatMessage(messages.lastHour),
        offset: 60,
      },
      {
        id: 'last6Hours',
        label: formatMessage(messages.last6Hours),
        offset: 60 * 6,
      },
      {
        id: 'last12Hours',
        label: formatMessage(messages.last12Hours),
        offset: 60 * 12,
      },
      {
        id: 'last24Hours',
        label: formatMessage(messages.last24Hours),
        offset: 60 * 24,
      },
    ],
    [formatMessage]
  );

  // default select last 24 hours for the TableDateSelector and first fetch
  const [selectedTimeRange, setSelectedTimeRange] = useState({
    timeRangeKind: PickerKinds.PRESET,
    timeRangeValue: timeRangeOptions[4],
  });

  const availableOwners = useMemo(() => users?.map(user => user.email), [users]);
  /** an array of alerts to update */
  const updateAlerts = useCallback(
    alerts => {
      let updatedAlerts;
      alerts.forEach(alert => {
        updatedAlerts = upsert(
          // use the latest or use the initial
          updatedAlerts || dashboardData || entityAlerts || hierarchyLevelAlerts,
          { alertId: alert.alertId },
          { ...alert, lastUpdatedBy: id }
        );
      });

      // Change the property for the component to show the change
      if (entityAlerts) {
        setEntityAlerts(updatedAlerts);
      } else if (hierarchyLevelAlerts) {
        setHierarchyLevelAlerts(updatedAlerts);
      }

      if (onCardAction) {
        // Change the property set on the card to be more consistent with the CardFetcher
        onCardAction(cardId, CARD_ACTIONS.UPDATE_DATA, { data: updatedAlerts });
      }
      return updateAlertsAPI(formatAlertsForHierarchyAPI(alerts), id);
    },
    [onCardAction, id, dashboardData, entityAlerts, hierarchyLevelAlerts, cardId]
  );

  const tableData = useMemo(
    () => hierarchyLevelAlerts || entityAlerts || [],
    [entityAlerts, hierarchyLevelAlerts]
  );

  /**
   * startDate: timestamp defaults to 24 hours
   * endDate: timestamp defaults to now
   */
  const refreshAlerts = useCallback(
    (startDate?: number, endDate?: number) => {
      // Default timestamp set to last 24 hours
      const defaultEndDate = moment();
      const defaultStartDate = defaultEndDate.clone().subtract(1, 'day');

      setIsFetchingData(true);
      fetchHierarchyAlerts(
        hierarchyPath,
        dataItemName ? { name: dataItemName } : null,
        startDate || defaultStartDate.valueOf(),
        endDate || defaultEndDate.valueOf(),
        'severity',
        null,
        false,
        id,
        undefined,
        currentUserResources
      )
        .then(hierarchyAlerts => {
          setHierarchyLevelAlerts(hierarchyAlerts);
          setIsFetchingData(false);
        })
        .catch(err => {
          console.error(err);
          setIsFetchingData(false);
        });
    },
    [dataItemName, hierarchyPath, id, currentUserResources]
  );

  const handleWorkorderCreation = useCallback(
    (workorder, alert) => {
      const workorderv2 = {
        siteName: workorder?.siteId,
        assetName: workorder?.assetId,
        locationName: workorder?.locationId,
        ...omit(workorder, ['siteId', 'assetId', 'locationId']),
      };

      try {
        return (
          hierarchyPath &&
          getHierarchyItemTypeFromHierarchyPath(hierarchyPath) === RESOURCE_TYPES.ASSET
            ? validateAssetRelationsAPI(
                {
                  assetName: workorder?.assetId,
                  locationName: workorder?.locationId,
                  siteName: workorder?.siteId,
                },
                id
              )
            : Promise.resolve()
        ).then(() =>
          createServiceRequestAPI(alert?.alertId, workorderv2, id).then(response => {
            updateAlerts([
              {
                alertId: alert?.alertId,
                actions: [
                  {
                    resultJson: response?.resultJson && JSON.stringify(response.resultJson),
                    launchUrl: response?.launchUrl,
                  },
                ],
              },
            ]);
          })
        );
      } catch (error) {
        return Promise.reject(error);
      } // if an exception is thrown don't close the panel
    },
    [hierarchyPath, id, updateAlerts]
  );

  const handleShowWorkorderDialog = useCallback(
    alert => {
      const hierarchyItemType = getHierarchyItemTypeFromHierarchyPath(hierarchyPath);
      if (hierarchyPath && hierarchyItemType === RESOURCE_TYPES.ORGANIZATION) {
        // For org level alert, Asset, Location and Site fields are empty free text fields
        actions.openDialog(DIALOG_IDS.CREATE_WORKORDER_MODAL, {
          alert,
          services,
          assets: [],
          locations: [],
          sites: [],
          // For system level and above, the only way to assemble the dashboard link is through the current URL path, assuming this table is being viewed in Monitor page
          // If it is being viewed in Setup page, the complete hierarchy path (one that includes org) is not available
          dashboardLink: window.location.pathname.includes('/organizations/')
            ? `${window.location.protocol}//${window.location.host}${window.location.pathname}`
            : '',
          onCreate: workorder => handleWorkorderCreation(workorder, alert),
        });
      } else if (
        hierarchyPath &&
        (hierarchyItemType === RESOURCE_TYPES.SITE || hierarchyItemType === RESOURCE_TYPES.SYSTEM)
      ) {
        // For site or system level alert, Asset and Location are empty free text fields. Site is pre-filled and read-only.
        const siteUUId = getSiteFromHierarchyPath(hierarchyPath);
        getSiteByIdAPI(siteUUId).then(site => {
          actions.openDialog(DIALOG_IDS.CREATE_WORKORDER_MODAL, {
            alert,
            services,
            assets: [],
            locations: [],
            sites: [site],
            // For system level and above, the only way to assemble the dashboard link is through the current URL path, assuming this table is being viewed in Monitor page
            // If it is being viewed in Setup page, the complete hierarchy path (one that includes org) is not available
            dashboardLink: window.location.pathname.includes('/organizations/')
              ? `${window.location.protocol}//${window.location.host}${window.location.pathname}`
              : '',
            onCreate: workorder => handleWorkorderCreation(workorder, alert),
          });
        });
      } else if (hierarchyPath && hierarchyItemType === RESOURCE_TYPES.LOCATION) {
        // For location level alert, Asset, Location and Site are pre-filled and read-only. The site is pre-filled with either asset's or location's siteName.
        // If location has more than one nested asset, the Asset will be a combobox.
        const siteUUId = getSiteFromHierarchyPath(hierarchyPath);
        const locationUUId = getLeafIdFromHierarchyPath(hierarchyPath);
        getLocationByIdForSiteAPI(siteUUId, locationUUId).then(location => {
          getAssetsForLocationAPI(siteUUId, locationUUId).then(assets => {
            actions.openDialog(DIALOG_IDS.CREATE_WORKORDER_MODAL, {
              alert,
              services,
              assets: assets?.results?.length > 0 ? assets.results : [],
              locations: [location],
              sites: [],
              dashboardLink: `${window.location.protocol}//${window.location.host}${ROUTES.MONITOR}${hierarchyPath}/dashboards/${location.name}`,
              onCreate: workorder => handleWorkorderCreation(workorder, alert),
            });
          });
        });
      } else if (hierarchyPath && hierarchyItemType === RESOURCE_TYPES.ASSET) {
        // For asset level alert, Asset, Location and Site are pre-filled and read-only. The site is pre-filled with the asset's siteName.
        const siteUUId = getSiteFromHierarchyPath(hierarchyPath);
        const assetUUId = getLeafIdFromHierarchyPath(hierarchyPath);
        getAssetByIdForSiteAPI(siteUUId, assetUUId).then(asset => {
          actions.openDialog(DIALOG_IDS.CREATE_WORKORDER_MODAL, {
            alert,
            services,
            assets: [asset],
            locations: [],
            sites: [],
            dashboardLink: `${window.location.protocol}//${window.location.host}${ROUTES.MONITOR}${hierarchyPath}/dashboards/${asset.name}`,
            onCreate: workorder => handleWorkorderCreation(workorder, alert),
          });
        });
      } else {
        // For device level alert, Asset, Location and Site are pre-filled and read-only. The site is pre-filled with the asset's siteName.
        // If device has more than one parent asset, the Asset will be a combobox and both Location and Site will be pre-filled with the selected asset's parentLocationName and siteName.
        // If device has more than one parent location, the Location will be a combobox.
        const deviceTypeUUId = hierarchyPath
          ? getDeviceTypeFromHierarchyPath(hierarchyPath)
          : entityTypeUUId;
        const deviceId = alert?.entityId ?? alert?.deviceDto?.name;
        const path = `/deviceTypes/${deviceTypeUUId}/devices/${deviceId}`;

        getDimensionsForDeviceAPI(path).then(dimensions =>
          getParentAssetsForDeviceAPI(deviceTypeUUId, deviceId).then(assets => {
            getParentLocationsForDeviceAPI(deviceTypeUUId, deviceId).then(locations => {
              actions.openDialog(DIALOG_IDS.CREATE_WORKORDER_MODAL, {
                dimensions,
                alert,
                services,
                assets: assets ?? [],
                locations: assets?.length === 0 ? locations ?? [] : [],
                sites: [],
                dashboardLink: `${window.location.protocol}//${window.location.host}${
                  ROUTES.MONITOR
                }/device-types/${
                  alert?.entityTypeName ??
                  alert?.deviceDto?.deviceTypeName ??
                  alert?.resourceDto?.name
                }/devices/${deviceId}`,
                onCreate: workorder => handleWorkorderCreation(workorder, alert),
              });
            });
          })
        );
      }
    },
    [actions, services, handleWorkorderCreation, entityTypeUUId, hierarchyPath]
  );

  const handleShowChangeStatusDialog = useCallback(
    alertIds => {
      actions.openDialog(DIALOG_IDS.CHANGE_ALERT_STATUS_MODAL, {
        availableStatusValues: domains?.Status.map(status => status.value),
        alertIds,
        onChangeStatus: (status, alertIdsToChange) =>
          updateAlerts(alertIdsToChange.map(alertId => ({ alertId, domainStatus: status }))),
      });
    },
    [actions, domains, updateAlerts]
  );

  const handleShowChangeOwnerDialog = useCallback(
    alertIds => {
      actions.openDialog(DIALOG_IDS.CHANGE_ALERT_OWNER_MODAL, {
        availableOwners: users,
        alertIds,
        onChangeOwner: (owner, alertIdsToChange) =>
          updateAlerts(alertIdsToChange.map(alertId => ({ alertId, owner }))),
      });
    },
    [actions, users, updateAlerts]
  );

  const handleDateChange = useCallback(
    ({ startDate, endDate }) => refreshAlerts(startDate, endDate),
    [refreshAlerts]
  );

  // Just do this once when the container startsup (don't need to do this in preview mode)
  useEffect(() => {
    if (!isPreview) {
      getOtherServices();
    }
  }, [getOtherServices, isPreview]);

  useEffect(() => {
    if (!isPreview && !isFetchingDomainData && isEmpty(domains)) {
      fetchDomains();
    }
  }, [domains, fetchDomains, isFetchingDomainData, isPreview]);

  useEffect(() => {
    if (!isPreview) {
      loadTenantMembers();
    }
  }, [isPreview, loadTenantMembers]);

  useEffect(() => {
    if (!isPreview && hasBetaPreviewEnabled()) {
      // We are using the dimension names in alert table view usecase. Only when alert views are enabled lets call this api
      // i.e assetTypes object will have assetTypes.dimensionNames property and determineAvailableColumnsForAlertsTable() method will behave differnetly.
      fetchEntityTypesData();
    }
  }, [deviceTypes, fetchEntityTypesData, isPreview]);

  useEffect(
    () => {
      // If we don't have dashboard data then we need to load data from redux
      if (isNil(dashboardData)) {
        refreshAlerts();
      }
    },
    [dataItemName] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <>
      <AlertsTable
        entityTypes={hasBetaPreviewEnabled() ? assetTypes : entityTypes}
        data={tableData}
        className={classnames(className, 'alertsTableContainer')}
        dashboardData={dashboardData}
        // throw away the table any time we switch between data items or entities
        key={dataItemName || entityId || entityTypeId || 'global'}
        hasAlertName={isNil(dataItemName)}
        onUpdateAlerts={updateAlerts}
        severityDomains={domains?.Severity}
        statusDomains={domains?.Status}
        availableOwners={availableOwners}
        onDateChange={handleDateChange}
        isExpanded={isExpanded}
        maximoServices={services}
        onCreateWorkorder={handleShowWorkorderDialog}
        onBulkChangeStatus={handleShowChangeStatusDialog}
        onBulkChangeOwner={handleShowChangeOwnerDialog}
        isPreview={isPreview}
        testId={testId}
        hasLargeNoData={hasLargeNoData}
        title={title}
        tooltip={tooltip}
        customToolbarContent={customToolbarContent}
        size={size}
        isFetchingData={isFetchingData}
        isFetchingAdditionalData={isFetchingAdditionalData}
        dashboardEditorColumns={columnsState}
        hasShortRows={hasShortRows}
        numberOfRowsPerPage={numberOfRowsPerPage}
        userId={id}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
        hasTableViewManagement={hasTableViewManagement}
        selectedTimeRange={selectedTimeRange}
        setSelectedTimeRange={setSelectedTimeRange}
        timeRangeOptions={timeRangeOptions}
        isHierarchyDashboard={isHierarchyDashboard}
        accessControls={accessControls}
      />
      <CreateServiceRequestModal />
      <ChangeAlertStatusModal />
      <ChangeAlertOwnerModal />
    </>
  );
};

const mapStateToProps = (state: RootState): Partial<AlertsTableContainerType> => ({
  // needed for alerts
  // severity and status of alerts
  domains: getDomains(state),
  assetTypes: getEntityTypes(state),
  users: getTenantMembers(state),
  currentUser: getCurrentUser(state),
  currentUserResources: getCurrentUserResources(state),
  services: getMaximoServices(state),
  isFetchingAdditionalData:
    isOtherServicesFetchingData(state) ||
    isDomainsFetching(state) ||
    getTenantMembersIsLoading(state) ||
    getEntityTypesFetchingData(state),
  isFetchingDomainData: isDomainsFetching(state),
  accessControls: getCurrentUserAccessControls(state),
  deviceTypes: getDeviceTypesData(state),
});

const mapDispatchToProps = (dispatch: AppDispatch): Partial<AlertsTableContainerType> => ({
  domainActions: bindActionCreators<any, any>(domainActionCreators, dispatch),
  entityTypeActions: bindActionCreators<any, any>(entityTypeActions, dispatch),
  userActions: bindActionCreators<any, any>(userActionCreators, dispatch),
  serviceActions: bindActionCreators<any, any>(serviceActionCreators, dispatch),
  actions: bindActionCreators<any, any>(commonActions, dispatch),
});

AlertsTableContainer.defaultProps = defaultProps;

export default connect(mapStateToProps, mapDispatchToProps)(AlertsTableContainer as any);
